import React, { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { HoroscopesGrid } from '@moment/components/HoroscopesV2/HoroscopesGrid/HoroscopesGrid';
import { HoroscopesCarousel } from '@moment/components/HoroscopesV2/HoroscopesCarousel/HoroscopesCarousel';
import { HoroscopeIconType } from '@moment/components/Horoscopes/HoroscopesIcon/HoroscopesIcon-Constants';
import { genFetchHoroscopesQuery } from '@moment/net/classic-fotoscapes/fetch-horoscopes';
import ActivityIndicator from '@moment/components/ActivityIndicator/ActivityIndicator';
import localstorage from '@/pages/Root/services/storage/localstorage';
import {
  getClickHistory,
  isHoroscopeCacheValid,
  recordSignClick,
  sortSignsByClickHistory,
} from '@moment/components/HoroscopesV2/HoroscopesV2Util';

import './HoroscopesV2Wrapper.scss';

interface HoroscopesV2WrapperProps {
  queryFn?: (params: { sign?: string; lang?: string }) => Promise<any>;
  handleWidgetClick?: (target: string) => void;
}

const HOROSCOPES_STORAGE_KEY = 'horoscopes-data';

export const HoroscopesV2Wrapper: React.FC<HoroscopesV2WrapperProps> = ({
  queryFn,
  handleWidgetClick,
}) => {
  const { sign } = useParams<{ sign: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [cachedData, setCachedData] = useState<any>(null);
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true);

  // Check localStorage for cached data (only use if content period has not ended)
  useEffect(() => {
    const storageKey = `${HOROSCOPES_STORAGE_KEY}-${lang}`;
    const cached = localstorage?.get(storageKey);
    if (cached && isHoroscopeCacheValid(cached)) {
      setCachedData(cached);
    }
    setIsLoadingFromCache(false);
  }, [lang]);

  // Generate queries for all horoscope categories
  const queriesToRun = useMemo(() => {
    // If we have cached data, return empty array to skip queries
    if (cachedData) {
      return [];
    }
    // Use custom queryFn if provided, otherwise use default
    if (queryFn) {
      // Create queries using custom queryFn
      return [
        {
          queryKey: ['horoscopes', 'general', lang],
          queryFn: () => queryFn({ lang }),
        },
        {
          queryKey: ['horoscopes', 'love', lang],
          queryFn: () => queryFn({ lang }),
        },
        {
          queryKey: ['horoscopes', 'money', lang],
          queryFn: () => queryFn({ lang }),
        },
        {
          queryKey: ['horoscopes', 'health', lang],
          queryFn: () => queryFn({ lang }),
        },
      ];
    }
    return genFetchHoroscopesQuery({ lang });
  }, [lang, cachedData, queryFn]);

  // Execute all queries in parallel (only if no cached data)
  const queryResults = useQueries({
    queries: queriesToRun,
  });

  // Store data in localStorage when queries complete
  useEffect(() => {
    if (!cachedData && queryResults.length > 0) {
      const allLoaded = queryResults.every(
        (result) => !result.isLoading && result.data
      );
      const hasError = queryResults.some((result) => result.error);

      if (allLoaded && !hasError) {
        const dataToCache = queryResults.map((result) => result.data);
        const storageKey = `${HOROSCOPES_STORAGE_KEY}-${lang}`;
        localstorage?.set(storageKey, dataToCache);
        setCachedData(dataToCache);
      }
    }
  }, [queryResults, cachedData, lang]);

  // Use cached data if available, otherwise use query results
  const generalData = useMemo(() => {
    if (cachedData) {
      return cachedData[0]; // First item is general data
    }
    return queryResults[0]?.data;
  }, [cachedData, queryResults]);

  const isLoading = useMemo(() => {
    if (isLoadingFromCache) return true;
    if (cachedData) return false;
    return queryResults.some((result) => result.isLoading);
  }, [isLoadingFromCache, cachedData, queryResults]);

  const hasError = useMemo(() => {
    if (cachedData) return false;
    return queryResults.some((result) => result.error);
  }, [cachedData, queryResults]);

  // Prepare horoscopes data for Grid component, sorted by click history
  const horoscopesForGrid = useMemo(() => {
    if (!generalData || !generalData.items) return [];

    // Get all signs sorted by click history
    const allSigns = Object.values(HoroscopeIconType);
    const sortedSigns = sortSignsByClickHistory(allSigns);

    return sortedSigns.map((sign) => {
      const translatedSign = t(sign, sign)?.toLowerCase();
      const matchingItem = generalData.items.find(
        (item: any) => item.horoscope?.sign?.toLowerCase() === translatedSign
      );

      return {
        sign,
        text:
          matchingItem?.horoscope.horoscopetext?.[lang] ||
          matchingItem?.horoscope.horoscopetext?.['en'] ||
          t('horoscope.noText', 'No horoscope text available'),
      };
    });
  }, [generalData, lang, t]);

  // Get sorted signs for Carousel component
  const sortedSignsForCarousel = useMemo(() => {
    const allSigns = Object.values(HoroscopeIconType);
    return sortSignsByClickHistory(allSigns);
  }, []);

  // Check if user has clicked on any signs
  // Recalculate when sign changes (which happens after a click)
  const hasClickHistory = useMemo(() => {
    const clickHistory = getClickHistory();
    return clickHistory.length > 0;
  }, [sign]);

  // Handle sign click - record it and navigate
  const handleSignClick = (selectedSign: HoroscopeIconType) => {
    recordSignClick(selectedSign);
    navigate({
      pathname: `/horoscope/${selectedSign}`,
      search: searchParams.size > 0 ? `?${searchParams.toString()}` : ``,
    });
    if (handleWidgetClick) {
      handleWidgetClick(selectedSign);
    }
  };

  if (isLoading) {
    return (
      <div className="horoscopes-v2-wrapper">
        <div className="horoscopes-v2-wrapper__loading">
          <ActivityIndicator />
          <p>{t('horoscope.loading', 'Loading horoscope...')}</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="horoscopes-v2-wrapper">
        <div className="horoscopes-v2-wrapper__error">
          <p>{t('horoscope.error', 'Error loading horoscope data')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="horoscopes-v2-wrapper">
      <div className="horoscopes-v2-wrapper__container">
        {/* Show HoroscopesGrid if user has clicked on signs, otherwise show HoroscopesCarousel */}
        {hasClickHistory ? (
          <div className="horoscopes-v2-wrapper__grid">
            <HoroscopesGrid
              horoscopes={horoscopesForGrid}
              onCardClick={handleSignClick}
            />
          </div>
        ) : (
          <div className="horoscopes-v2-wrapper__carousel">
            <HoroscopesCarousel
              signOrder={sortedSignsForCarousel}
              onTileClick={handleSignClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};
